import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { isEqual } from 'lodash'
import { capitalCase } from 'change-case'
import Recipe = Models.Recipe
import { useUser } from '../../hooks/useUser'
import { axiosPost } from '../../util/api'
import { TargetACOS } from '../TargetACOS'
import {
    cents,
    money,
    nullableMoney,
    nullableNumber,
    nullablePercentage,
    number,
    percentage,
} from '../../util/formatters'
import { useFirstRender } from '../../hooks/useFirstRender'
import { UpdateReportAdTypes } from '../../util/contstants'
import { AcosCell, BlendedAcosCell } from '../table/AcosCell'
import { BlendedRoasCell, RoasCell } from '../table/RoasCell'
import { KDPPerformanceColumns } from '../table/kdp_performance_columns'
import { PerformanceColumns } from '../table/performance_columns'
import { add_kdp_cols, get, getSuffix } from '../../util/kdp_cols'
import { useQueryFlag } from '../../hooks/useQueryFlag'
import { recipeStore } from '../../stores/recipe_store'
import { Toasts } from '../toaster/Toast'
import {
    Columns as AgGridColumns,
    type CellProps,
    type ExpandedPerformanceMetrics,
} from '../table2/ad_grid_columns'
import { AgGridColumnDef, AgGridTable, DummyHeader } from '../table2/AgGridTable'
import { TableStore } from '../table2/table_store'
import { userStore } from '../../stores/user_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { RenderKeywordName, RenderKeywordOrTargetName, RenderTargetName } from '../../columns'
import { AmazonLink, AmazonSearchLink, isAsin } from '../amazon_link'
import { Tooltip } from '../ui/tooltip'
import { RelativeTimestamp } from '../ui/RelativeTimestamp'
import { TooltipContent } from '../table/TooltipHeader'
import { Marketplaces } from '../../util/marketplaces'
import { syntheticTargetMatchType } from '../../util/target_refinements'
import { BaseBadge } from '../badge/BaseBadge'
import type { Intent } from '../../types/badges'
import { deliveryReasonOptions, type DeliveryReason } from './delivery-status'

type Profile = Models.Profile

interface PreviewTableProps {
    recipe: Recipe
    validTrigger: boolean
    onReloadRef?: React.MutableRefObject<(() => void) | null>
    backend?: string | null
    fixedHeight?: number
}

type PreviewItem = any

export const usesV5RecipePreview = (adType: number): boolean =>
    [
        UpdateReportAdTypes.SearchTerms,
        UpdateReportAdTypes.KeywordsAndTargets,
        UpdateReportAdTypes.Keywords,
        UpdateReportAdTypes.Targets,
        UpdateReportAdTypes.ProductAds,
        UpdateReportAdTypes.AdGroups,
        UpdateReportAdTypes.Campaigns,
        UpdateReportAdTypes.CampaignNegativeKeywords,
        UpdateReportAdTypes.NegativeKeywords,
        UpdateReportAdTypes.NegativeTargets,
    ].includes(adType)

interface VariableConfig {
    name: string
    type: 'number' | 'string' | 'boolean' | 'state' | 'date' | 'time_period'
    formula: string
}

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

export const PreviewTable = observer(
    ({ recipe, validTrigger, onReloadRef, backend, fixedHeight }: PreviewTableProps) => {
        const { recipeId } = useParams()
        const { trigger } = recipe
        const { activeProfile } = useUser()
        const [times, setTimes] = useState<string[]>([])
        const [pendingRefresh, setPendingRefresh] = useState(false)
        const [previewError, setPreviewError] = useState<string | null>(null)
        const tableHeight = fixedHeight ? fixedHeight : 400 // Full height since header was removed
        const firstRender = useFirstRender()
        const useExp = useQueryFlag('useExp')
        const explain = useQueryFlag('explain')
        const version = recipeStore.version
        const [variables, setVariables] = useState<Record<string, VariableConfig>>({})
        const [store] = useState(new TableStore<PreviewItem>(userStore, 'RecipePreview'))
        const savedFiltersStore = useSavedFiltersStore()

        const preview = (params: any) => {
            // If we have an existing recipe we are looking at, only load the preview
            // once we have pulled in the recipe data.
            if (recipeId != 'new' && (recipe.id ?? 0) != parseInt(recipeId)) {
                return Promise.resolve({ data: { data: [] } })
            }
            if (!activeProfile) {
                return Promise.resolve({ data: { data: [] } })
            }

            setPreviewError(null)

            if (version == 1) {
                return axiosPost(
                    'recipes/preview',
                    { ...params, use_exp: useExp, explain, ...(backend && { backend }) },
                    null,
                    {
                        ignore: true,
                    },
                )
                    .then((res) => {
                        setTimes(res.data.times)
                        res.data.data = res.data.items
                        setPendingRefresh(false)
                        setPreviewError(null)

                        return res
                    })
                    .catch((err) => {
                        const msg = err.response?.data?.message ?? 'Failed to load preview'
                        setPreviewError(msg)
                        setPendingRefresh(false)
                        throw err
                    })
            } else {
                let url = 'api/v4/recipes/v2/preview'
                const useNewBackend = usesV5RecipePreview(params.class)
                if (useNewBackend) {
                    url = 'api/v5/recipes/preview'
                }

                return axiosPost(
                    url,
                    {
                        ...params,
                        use_exp: useExp,
                        explain,
                        action: recipeStore.recipe.action_params,
                        ...((backend === 'duckdb' || backend === 'core' || backend === 'slate') && {
                            backend,
                        }),
                    },
                    true,
                    {
                        ignore: true,
                    },
                )
                    .then((res) => {
                        setTimes(res.data.times)
                        res.data.data = res.data.items
                        setPendingRefresh(false)
                        setPreviewError(null)

                        // Only update variables if they have changed
                        const new_variables: Record<string, VariableConfig> =
                            res.data?.variables ?? {}
                        if (!isEqual(variables, new_variables)) {
                            setVariables(new_variables)
                        }

                        return res
                    })
                    .catch((err) => {
                        if (
                            useNewBackend &&
                            err.response?.data?.error?.code === 'preview_superseded'
                        ) {
                            throw err
                        }

                        // The grid persists its sort under one TableStore key,
                        // `RecipePreview`, and restores it from localStorage before
                        // the first request. That key outlives the ad type and the
                        // trigger variable it came from, so a stale key made the
                        // builder refuse the sort and fail the whole preview — on
                        // every later attempt, until the user changed it by hand.
                        //
                        // The client cannot decide this from its own columns:
                        // `times` and `variables` only arrive with the first
                        // response, so on the first render the metric and variable
                        // columns do not exist yet and a valid saved sort would look
                        // stale. Recover from the server's answer instead, and clear
                        // the key so it stops coming back. Clearing the sort reloads
                        // without it; the guard on `store.params.sort` means that
                        // retry cannot recurse.
                        if (store.params.sort && isUnsupportedSortError(err)) {
                            store.setSort(undefined)
                            setPendingRefresh(false)

                            return { data: { data: [] } }
                        }

                        // This route reports the reason under `error`; `message` is
                        // never set, so every failure used to read "Failed to load
                        // preview". `error` is an object on some paths (see the
                        // superseded check above), so only take it when it is text.
                        const reason = err.response?.data?.error
                        const msg =
                            (typeof reason === 'string' ? reason : undefined) ??
                            err.response?.data?.message ??
                            'Failed to load preview'
                        Toasts.error(msg)
                        setPreviewError(msg)
                        setPendingRefresh(false)
                        throw err
                    })
            }
        }

        const tableHooks = useTableHooksFromStore(
            (params: any) => {
                return preview({
                    ...params,
                    trigger,
                    class: recipe.ad_type,
                })
            },
            {},
            false,
            store,
        )

        // Expose reload function through ref
        useEffect(() => {
            if (onReloadRef) {
                onReloadRef.current = () => {
                    tableHooks.reload()
                    setPendingRefresh(false)
                }
            }
        }, [onReloadRef, tableHooks])

        // Update MobX store with preview stats
        useEffect(() => {
            recipeStore.setPreviewStats({
                total: tableHooks.pagination.total,
                loading: tableHooks.loading,
                error: previewError,
            })

            // Reset pending refresh when data finishes loading
            if (!tableHooks.loading) {
                recipeStore.setPendingRefresh(false)
            }
        }, [tableHooks.pagination.total, tableHooks.loading, previewError])

        useEffect(() => {
            if (recipe.ad_type && !firstRender) {
                tableHooks.reload()
            }
        }, [recipe.ad_type])

        useEffect(() => {
            if (!firstRender && validTrigger) {
                setPendingRefresh(true)
            }
        }, [trigger])

        const columns = useMemo<AgGridColumnDef<PreviewItem>[]>(() => {
            return resolveColumns(recipe.ad_type, activeProfile, times, variables, version, {
                showBidPreview: recipeStore.showBidPreview,
                showDefaultBidPreview: recipeStore.showDefaultBidPreview,
                showBudgetPreview: recipeStore.showBudgetPreview,
            })
        }, [
            activeProfile,
            recipe.ad_type,
            times,
            variables,
            version,
            recipeStore.showBidPreview,
            recipeStore.showDefaultBidPreview,
            recipeStore.showBudgetPreview,
        ])

        return (
            <div
                className="relative box-border overflow-hidden px-6 py-3"
                style={{
                    height: tableHeight, // Full height since header was removed
                }}
            >
                {/* Individual resize handle removed - controlled by parent */}
                <AgGridTable
                    name="RecipePreview"
                    store={store}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={tableHooks}
                    columns={columns}
                    showHeader={false}
                    search
                    searchPlaceholder="Search by keyword, ad group, or campaign"
                    selectKey={resolvePreviewSelectKey(recipe.ad_type) ?? undefined}
                    columnSelection
                    dateSelect={false}
                    resizableColumns
                    fitParent
                />
            </div>
        )
    },
)

/**
 * Recognise the preview builder refusing a sort column.
 *
 * The route answers HTTP 400 with `{error, code, type}` when the MoonBit builder
 * cannot ORDER BY the requested column.
 */
export const isUnsupportedSortError = (err: any): boolean =>
    typeof err?.response?.data?.error === 'string' &&
    err.response.data.error.includes('Unsupported sort column')

interface ColumnOptions {
    showBidPreview?: boolean
    showDefaultBidPreview?: boolean
    showBudgetPreview?: boolean
}

// A preview row's id field depends on the recipe's ad type. This table used
// to hardcode "keyword_id" for every type (MER-3119).
export const resolvePreviewSelectKey = (adType: number): keyof PreviewItem | null => {
    switch (adType) {
        case UpdateReportAdTypes.Campaigns:
            return 'campaign_id'
        case UpdateReportAdTypes.AdGroups:
            return 'ad_group_id'
        case UpdateReportAdTypes.Keywords:
            return 'keyword_id'
        case UpdateReportAdTypes.Targets:
            return 'target_id'
        case UpdateReportAdTypes.ProductAds:
            return 'ad_id'
        case UpdateReportAdTypes.CampaignNegativeKeywords:
        case UpdateReportAdTypes.NegativeKeywords:
            return 'keyword_id'
        case UpdateReportAdTypes.NegativeTargets:
            return 'target_id'
        // KeywordsAndTargets rows mix keyword_id and target_id per row.
        // SearchTerms rows have no reliable per-row id field. Leave
        // selection unsupported for both rather than guess wrong.
        case UpdateReportAdTypes.KeywordsAndTargets:
        case UpdateReportAdTypes.SearchTerms:
            return null
        default:
            console.error('No preview select key mapping for ad type:', adType)
            return null
    }
}

export const resolveColumns = (
    type: number,
    activeProfile: Profile | null,
    times: string[],
    variables: Record<string, VariableConfig>,
    version: number,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    switch (type) {
        case UpdateReportAdTypes.Campaigns:
            return resolveCampaignColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.AdGroups:
            return resolveAdGroupColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.Keywords:
            return resolveKeywordColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.Targets:
            return resolveTargetColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.ProductAds:
            return resolveProductAdColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.KeywordsAndTargets:
            return resolveKeywordAndTargetColumns(activeProfile, times, version, variables, options)
        case UpdateReportAdTypes.SearchTerms:
            return resolveSearchTermColumns(activeProfile, times, version, variables, options)
        default:
            console.error('Cannot resolve columns for type:', type)
            return []
    }
}

const cloneColumn = (
    column: AgGridColumnDef<any>,
    width?: number,
): AgGridColumnDef<PreviewItem> => ({
    ...column,
    ...(width !== undefined ? { width } : {}),
})

const CampaignName = () => cloneColumn(AgGridColumns.CampaignName, 400)
const AdGroupName = () => cloneColumn(AgGridColumns.AdGroupName, 250)
const ASIN = () => cloneColumn(AgGridColumns.ASIN)
const ProductAdsCount = () => cloneColumn(AgGridColumns.ProductAdsCount)
const Parent = () => cloneColumn(AgGridColumns.Parent, 400)
const SmartBidToggle = () => cloneColumn(AgGridColumns.SmartBidToggle, 90)
const StartDate = () => cloneColumn(AgGridColumns.StartDate, 100)
const EndDate = () => cloneColumn(AgGridColumns.EndDate, 100)
const TargetCPC = () => cloneColumn(AgGridColumns.TargetCPC, 100)

const deliveryStatusOptions: Record<string, { text: string; intent: Intent }> = {
    DELIVERING: { text: 'Delivering', intent: 'success' },
    NOT_DELIVERING: { text: 'Not Delivering', intent: 'warning' },
    UNAVAILABLE: { text: 'Unavailable', intent: 'none' },
    OTHER: { text: 'Other', intent: 'none' },
}

export const deliveryReason = (reason: string): DeliveryReason =>
    deliveryReasonOptions[reason] ?? {
        label: capitalCase(reason) || 'Unknown Delivery Reason',
        tier: 'ACTIONABLE',
    }

export const DeliveryStatus = (): AgGridColumnDef<PreviewItem> => ({
    field: 'delivery_status',
    headerName: 'Delivery Status',
    headerComponent: agGridComponent(DummyHeader),
    tooltip: (
        <TooltipContent
            title="Delivery Status"
            description="Amazon's ad-serving status. Sponsored Products only. This column is blank for Sponsored Brands and Sponsored Display."
        />
    ),
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        const status = data.delivery_status
        if (typeof status !== 'string' || !deliveryStatusOptions[status]) {
            return status ? <span>{capitalCase(String(status))}</span> : null
        }

        return <BaseBadge options={deliveryStatusOptions} value={status} />
    }),
})

export const DeliveryReasons = (): AgGridColumnDef<PreviewItem> => ({
    field: 'delivery_reasons',
    headerName: 'Delivery Reasons',
    headerComponent: agGridComponent(DummyHeader),
    tooltip: (
        <TooltipContent
            title="Delivery Reasons"
            description="Why Amazon is not delivering this entity. Reasons you can fix appear first. Inherited parent state, such as a paused or archived campaign, appears in grey."
        />
    ),
    width: 240,
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        if (!Array.isArray(data.delivery_reasons)) return null

        const reasons = data.delivery_reasons
            .filter((reason: unknown): reason is string => typeof reason === 'string')
            .map(deliveryReason)
        if (reasons.length === 0) return null

        const orderedReasons = [
            ...reasons.filter((reason) => reason.tier === 'ACTIONABLE'),
            ...reasons.filter((reason) => reason.tier === 'INHERITED'),
        ]
        const visibleReasons = orderedReasons.slice(0, 1)
        const overflowCount = orderedReasons.length - visibleReasons.length
        return (
            <Tooltip
                content={
                    <div style={{ whiteSpace: 'pre-line' }}>
                        • {orderedReasons.map((reason) => reason.label).join('\n• ')}
                    </div>
                }
            >
                <span className="flex min-w-0">
                    {visibleReasons.map((reason) => (
                        <span
                            key={reason.label}
                            className={`min-w-0 flex-1 truncate${
                                reason.tier === 'INHERITED' ? ' text-gray-500' : ''
                            }`}
                        >
                            {reason.label}
                        </span>
                    ))}
                    {overflowCount > 0 && (
                        <span className="ml-1 shrink-0 text-gray-500">+{overflowCount} more</span>
                    )}
                </span>
            </Tooltip>
        )
    }),
})

const DailyBudget = (version: number): AgGridColumnDef<PreviewItem> => ({
    field: 'daily_budget',
    headerName: 'Budget',
    headerComponent: agGridComponent(DummyHeader),
    align: 'right',
    cellClass: 'justify-end',
    width: 110,
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        const value = data.daily_budget ?? data.budget
        return version === 2 ? money(value) : cents(value)
    }),
})

const DefaultBid = (version: number): AgGridColumnDef<PreviewItem> => ({
    field: 'default_bid',
    headerName: 'Bid',
    headerComponent: agGridComponent(DummyHeader),
    align: 'right',
    cellClass: 'justify-end',
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        return version === 2 ? money(data.default_bid) : cents(data.default_bid)
    }),
})

const Bid = (version: number): AgGridColumnDef<PreviewItem> => ({
    field: 'bid',
    headerName: 'Bid',
    headerComponent: agGridComponent(DummyHeader),
    align: 'right',
    cellClass: 'justify-end',
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        return version === 2 ? money(data.bid) : cents(data.bid)
    }),
})

const BidStrategy = (): AgGridColumnDef<PreviewItem> => ({
    field: 'bid_strategy',
    headerName: 'Bid Strategy',
    headerComponent: agGridComponent(DummyHeader),
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        if (data?.optimization?.bidStrategy) {
            return data.optimization.bidStrategy.replaceAll('_', ' ').toLowerCase()
        }

        return ''
    }),
})

const Keyword = (): AgGridColumnDef<PreviewItem> => ({
    ...AgGridColumns.Keyword,
    cellRenderer: agGridComponent(({ data, context }: CellProps<PreviewItem>) => (
        <RenderKeywordName
            data={data}
            screenshotMode={context.userHook.screenshotMode}
            mutateItems={(v: any) => context.store.setItems(v)}
            link
            smartBidSettings={false}
            fill
        />
    )),
})

const TargetingExpression = (): AgGridColumnDef<PreviewItem> => ({
    ...AgGridColumns.TargetingExpression,
    cellRenderer: agGridComponent(({ data, context }: CellProps<PreviewItem>) => (
        <RenderTargetName
            data={data}
            screenshotMode={context.userHook.screenshotMode}
            mutateItems={(v: any) => context.store.setItems(v)}
            link
            smartBidSettings={false}
            fill
        />
    )),
})

const KeywordOrTarget = (): AgGridColumnDef<PreviewItem> => ({
    field: 'target_details',
    headerName: 'Target',
    headerComponent: agGridComponent(DummyHeader),
    width: 300,
    cellRenderer: agGridComponent(({ data, context }: CellProps<PreviewItem>) => (
        <RenderKeywordOrTargetName
            data={data}
            screenshotMode={context.userHook.screenshotMode}
            mutateItems={(v: any) => context.store.setItems(v)}
            link
            smartBidSettings={false}
        />
    )),
})

/**
 * The preview's own Search Term cell. The Search Terms page cell gates on
 * `type_of`, which only that page's query emits, so it renders nothing for a
 * preview row. See MER-2808.
 */
export const SearchTermText = (): AgGridColumnDef<PreviewItem> => ({
    field: 'search_term',
    headerName: 'Search Term',
    headerComponent: agGridComponent(DummyHeader),
    width: 250,
    cellRenderer: agGridComponent(({ data, context }: CellProps<PreviewItem>) => {
        const term: string | undefined = data?.search_term

        if (!term) {
            return null
        }

        const screenshotMode = context.userHook.screenshotMode

        return (
            <div className="flex items-baseline" style={{ lineHeight: '21px' }}>
                <b>
                    {isAsin(term) ? (
                        <AmazonLink asin={term} screenshotMode={screenshotMode} />
                    ) : (
                        <AmazonSearchLink query={term} screenshotMode={screenshotMode} />
                    )}
                </b>
            </div>
        )
    }),
})

export const IsNegated = (): AgGridColumnDef<PreviewItem> => ({
    field: 'negative',
    headerName: 'Negated',
    headerComponent: agGridComponent(DummyHeader),
    width: 100,
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        const status = data.negative ? 'true' : 'false'

        if (!data.negative) {
            return (
                <Tooltip content="This search term is not negated.">
                    <span>{status}</span>
                </Tooltip>
            )
        }

        const negationDetails: string[] = []

        if (data.campaign_negatives && data.campaign_negatives.length > 0) {
            data.campaign_negatives.forEach((negative: any) => {
                if (negative.type === 'campaign_negative_keyword') {
                    const matchType =
                        negative.match_type === 1
                            ? 'Exact Match'
                            : negative.match_type === 2
                              ? 'Phrase Match'
                              : 'Broad Match'
                    negationDetails.push(`Campaign Negative ${matchType} Keyword`)
                } else if (negative.type === 'campaign_negative_target') {
                    negationDetails.push('Campaign Negative Target')
                }
            })
        }

        if (data.ad_group_negatives && data.ad_group_negatives.length > 0) {
            data.ad_group_negatives.forEach((negative: any) => {
                if (negative.type === 'ad_group_negative_keyword') {
                    const matchType =
                        negative.match_type === 1
                            ? 'Exact Match'
                            : negative.match_type === 2
                              ? 'Phrase Match'
                              : 'Broad Match'
                    negationDetails.push(`Ad Group Negative ${matchType} Keyword`)
                } else if (negative.type === 'ad_group_negative_target') {
                    negationDetails.push('Ad Group Negative Target')
                }
            })
        }

        if (data.target_negatives && data.target_negatives.length > 0) {
            data.target_negatives.forEach((negative: any) => {
                if (negative.type === 'ad_group_negative_target') {
                    negationDetails.push('Ad Group Negative Target')
                } else if (negative.type === 'campaign_negative_target') {
                    negationDetails.push('Campaign Negative Target')
                }
            })
        }

        const tooltipContent =
            negationDetails.length > 0
                ? negationDetails.join('\n• ')
                : 'This search term is negated.'

        return (
            <Tooltip
                content={
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {negationDetails.length > 0 && '• '}
                        {tooltipContent}
                    </div>
                }
            >
                <span>{status}</span>
            </Tooltip>
        )
    }),
})

const toMillisecondTimestamp = (v: number) => {
    if (v < 10000000000) {
        return v * 1000
    }

    return v
}

const LastDailyBudgetChange = (): AgGridColumnDef<PreviewItem> => ({
    ...AgGridColumns.LastDailyBudgetChange,
    headerName: 'Last Daily Budget Change',
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        const value = data.last_daily_budget_change ?? data.last_budget_change
        return value ? <RelativeTimestamp date={new Date(toMillisecondTimestamp(value))} /> : null
    }),
})

const LastBidChange = (): AgGridColumnDef<PreviewItem> => ({
    ...AgGridColumns.LastBidChange,
    cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
        return data.last_bid_change ? (
            <RelativeTimestamp date={new Date(toMillisecondTimestamp(data.last_bid_change))} />
        ) : null
    }),
})

const perfColumnsForTimes = (
    times: string[],
    activeProfile: Profile | null,
    version: number,
): AgGridColumnDef<PreviewItem>[] => {
    const cols: any[] = [...PerformanceColumns]

    if (activeProfile?.is_kdp) {
        cols.push(...KDPPerformanceColumns)
        add_kdp_cols(cols as any)
    }

    return formatColumnsForTimes(times, cols, version)
}

const formatterForLegacyColumn = (c: any) => {
    if (!c.formatter) {
        return undefined
    }

    if (c.formatter === number) {
        return ({ value }: { value: number | null | undefined }) =>
            value == null ? '' : (nullableNumber(value) ?? '')
    }
    if (c.formatter === money) {
        return ({ value }: { value: number | null | undefined }) =>
            value == null ? '' : (nullableMoney(value) ?? '')
    }
    if (c.formatter === percentage) {
        return ({ value }: { value: number | null | undefined }) =>
            value == null ? '' : (nullablePercentage(value) ?? '')
    }

    return ({ value }: { value: number }) => c.formatter(value) ?? ''
}

const performanceColumnMap: Record<string, AgGridColumnDef<any>> = {
    cpc: AgGridColumns.CPC,
    rpc: AgGridColumns.RPC,
    cost: AgGridColumns.Spend,
    sales: AgGridColumns.Sales,
    adjusted_sales: AgGridColumns.AdjustedSales,
    blended_profit: AgGridColumns.BlendedProfit,
    acos: AgGridColumns.ACOS,
    blended_acos: AgGridColumns.BlendedACOS,
    roas: AgGridColumns.ROAS,
    blended_roas: AgGridColumns.BlendedROAS,
    impressions: AgGridColumns.Impressions,
    clicks: AgGridColumns.Clicks,
    ctr: AgGridColumns.CTR,
    orders: AgGridColumns.Orders,
    adjusted_orders: AgGridColumns.AdjustedOrders,
    cvr: AgGridColumns.CVR,
    blended_cvr: AgGridColumns.BlendedCVR,
    cac: AgGridColumns.CAC,
    blended_cac: AgGridColumns.BlendedCAC,
    aov: AgGridColumns.AOV,
    blended_aov: AgGridColumns.BlendedAOV,
    pages_read: AgGridColumns.PagesRead,
    adjusted_pages_read: AgGridColumns.AdjustedPagesRead,
    estimated_royalties: AgGridColumns.EstimatedRoyalties,
    adjusted_estimated_royalties: AgGridColumns.AdjustedRoyalties,
    blended_rpc: AgGridColumns.BlendedRPC,
}

const performanceHeaderMap: Record<string, string> = {
    cpc: 'CPC',
    rpc: 'RPC',
    cost: 'Spend',
    sales: 'Sales',
    adjusted_sales: 'Adj Sales',
    blended_profit: 'Blended Profit',
    acos: 'ACOS',
    blended_acos: 'Blended ACOS',
    roas: 'ROAS',
    blended_roas: 'Blended ROAS',
    impressions: 'Impressions',
    clicks: 'Clicks',
    ctr: 'CTR',
    orders: 'Orders',
    adjusted_orders: 'Adj Orders',
    cvr: 'CVR',
    blended_cvr: 'Blended CVR',
    cac: 'CAC',
    blended_cac: 'Blended CAC',
    aov: 'AOV',
    blended_aov: 'Blended AOV',
    pages_read: 'Pages Read',
    adjusted_pages_read: 'Adj Pages Read',
    estimated_royalties: 'Estimated Royalties',
    adjusted_estimated_royalties: 'Adj ESt Royalties',
    blended_rpc: 'Blended RPC',
}

const getMarketplaceFromContext = (
    data: ExpandedPerformanceMetrics,
    context: CellProps<ExpandedPerformanceMetrics>['context'],
) => {
    let marketplace = data.marketplace
    // @ts-ignore
    if (context.showTableInBaseCurrency && context.baseCurrency) {
        // @ts-ignore
        marketplace = Object.values(Marketplaces).find(
            // @ts-ignore
            (m) => m.currency_code === context.baseCurrency,
        )
    }

    return marketplace
}

const legacyExtraForCell = (
    context: CellProps<PreviewItem>['context'],
    field: string,
    version: number,
) => ({
    hasActiveSubscription: context.userHook.hasActiveSubscription,
    screenshotMode: context.userHook.screenshotMode,
    impact_multiplier: context.userHook.activeProfile?.impact_multiplier,
    props: undefined,
    recipe_version: version,
    col: { key: field },
    reload: () => context.store.forceReload(),
    mutateItems: (v: any) => context.store.setItems(v),
})

const regularAcosRenderer = (version: number) =>
    agGridComponent(
        ({ data, context, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
            const suffix = getSuffix(colDef.field as string)
            const g = (key: string) => get(data, key, suffix)
            return AcosCell({
                acos: g('acos'),
                target_acos: data.target_acos,
                default_target_acos: data.default_target_acos,
                cost: g('cost'),
                marketplace: getMarketplaceFromContext(data, context),
                version,
            })
        },
    )

const regularRoasRenderer = (version: number) =>
    agGridComponent(
        ({ data, context, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
            const suffix = getSuffix(colDef.field as string)
            const g = (key: string) => get(data, key, suffix)
            return RoasCell({
                roas: g('roas'),
                acos: g('acos'),
                target_acos: data.target_acos,
                default_target_acos: data.default_target_acos,
                cost: g('cost'),
                marketplace: getMarketplaceFromContext(data, context),
                version,
            })
        },
    )

const blendedAcosRenderer = (version: number) =>
    agGridComponent(
        ({ data, context, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
            const suffix = getSuffix(colDef.field as string)
            const g = (key: string) => get(data, key, suffix)
            let acos = g('blended_acos')
            let target_acos = data.target_acos
            if (version === 2) {
                acos = acos * 100.0
                target_acos = target_acos ? target_acos * 10000.0 : target_acos
            }
            return BlendedAcosCell({
                ...data,
                target_acos,
                cost: g('cost'),
                acos,
                marketplace: getMarketplaceFromContext(data, context),
                version,
            })
        },
    )

const blendedRoasRenderer = (version: number) =>
    agGridComponent(
        ({ data, context, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
            const suffix = getSuffix(colDef.field as string)
            const g = (key: string) => get(data, key, suffix)
            let acos = g('blended_acos')
            let target_acos = data.target_acos
            if (version === 2) {
                acos = acos * 100.0
                target_acos = target_acos ? target_acos * 10000.0 : target_acos
            }
            return BlendedRoasCell({
                ...data,
                acos,
                target_acos,
                roas: g('blended_roas'),
                cost: g('cost'),
                marketplace: getMarketplaceFromContext(data, context),
                version,
            })
        },
    )

// A v2 preview sends these as decimals. The row cells scale them by 100; the
// header total has no cell, so it needs the same scale.
const v2DecimalRatioFields = new Set(['acos', 'ctr', 'cvr', 'blended_acos', 'blended_cvr'])

const formatColumnsForTimes = (
    times: string[],
    columns: any[],
    version: number,
): AgGridColumnDef<PreviewItem>[] => {
    const out: AgGridColumnDef<PreviewItem>[] = []

    columns.forEach((c) => {
        times.forEach((time) => {
            const baseField = c.key as string
            const baseDef = performanceColumnMap[baseField]

            if (!baseDef) {
                return
            }

            const field = `${baseField}_${time}`
            let cellRenderer = baseDef.cellRenderer
            if (baseField === 'acos') {
                cellRenderer = regularAcosRenderer(version)
            } else if (baseField === 'roas') {
                cellRenderer = regularRoasRenderer(version)
            } else if (baseField === 'blended_acos') {
                cellRenderer = blendedAcosRenderer(version)
            } else if (baseField === 'blended_roas') {
                cellRenderer = blendedRoasRenderer(version)
            } else if (c.cell) {
                cellRenderer = agGridComponent(
                    ({ data, context, column: { colDef } }: CellProps<PreviewItem>) =>
                        c.cell(data, legacyExtraForCell(context, colDef.field as string, version)),
                )
            }

            out.push({
                ...baseDef,
                field,
                headerName:
                    performanceHeaderMap[baseField] ?? baseDef.headerName ?? capitalCase(baseField),
                headerPrefix: capitalCase(time),
                valueFormatter: formatterForLegacyColumn(c),
                ...(version === 2 && v2DecimalRatioFields.has(baseField)
                    ? { headerFormatter: ({ value }) => percentage(value * 100) }
                    : {}),
                cellRenderer,
                width: c.width ? c.width + (time === 'lifetime' ? 40 : 10) : undefined,
            })
        })
    })

    return out
}

const resolveCampaignColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    let columns: AgGridColumnDef<PreviewItem>[] = [
        CampaignName(),
        SmartBidToggle(),
        StartDate(),
        EndDate(),
        ProductAdsCount(),
        DeliveryStatus(),
        DeliveryReasons(),
        DailyBudget(version),
        ...(options.showBudgetPreview ? [budgetPreview()] : []),
        LastDailyBudgetChange(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    if (version === 2) {
        columns = columns.filter((c) => c.field !== 'smart_bid' && c.field !== 'product_ads_count')

        columns.splice(5, 0, BidStrategy())
    }

    return columns
}

const resolveAdGroupColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    let columns: AgGridColumnDef<PreviewItem>[] = [
        AdGroupName(),
        ProductAdsCount(),
        DeliveryStatus(),
        DeliveryReasons(),
        DefaultBid(version),
        ...(options.showDefaultBidPreview ? [defaultBidPreview()] : []),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    if (version === 2) {
        columns = columns.filter((c) => c.field !== 'product_ads_count')
    }

    return columns
}

const resolveKeywordColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    const columns: AgGridColumnDef<PreviewItem>[] = [
        Keyword(),
        Parent(),
        ProductAdsCount(),
        DeliveryStatus(),
        DeliveryReasons(),
        Bid(version),
        ...(options.showBidPreview ? [bidPreview()] : []),
        LastBidChange(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    return columns
}

const resolveKeywordAndTargetColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    const columns: AgGridColumnDef<PreviewItem>[] = [
        KeywordOrTarget(),
        Parent(),
        ProductAdsCount(),
        DeliveryStatus(),
        DeliveryReasons(),
        Bid(version),
        ...(options.showBidPreview ? [bidPreview()] : []),
        LastBidChange(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    return columns
}

const resolveTargetColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    const columns: AgGridColumnDef<PreviewItem>[] = [
        TargetingExpression(),
        Parent(),
        ProductAdsCount(),
        DeliveryStatus(),
        DeliveryReasons(),
        Bid(version),
        ...(options.showBidPreview ? [bidPreview()] : []),
        LastBidChange(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    return columns
}

const resolveProductAdColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    const columns: AgGridColumnDef<PreviewItem>[] = [
        ASIN(),
        Parent(),
        DeliveryStatus(),
        DeliveryReasons(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
        TargetCPC(),
    ]

    addTargetAcos(columns, times[0], version)

    return columns
}

const resolveSearchTermColumns = (
    activeProfile: Profile | null,
    times: string[],
    version: number,
    variables: Record<string, VariableConfig>,
    options: ColumnOptions,
): AgGridColumnDef<PreviewItem>[] => {
    const columns: AgGridColumnDef<PreviewItem>[] = [
        SearchTermText(),
        IsNegated(),
        Parent(),
        ...generateVariableColumns(variables),
        ...perfColumnsForTimes(times, activeProfile, version),
    ]

    addTargetAcos(columns, times[0], version)

    return columns
}

const moneyWithMoreDecimals = (v: number) => {
    return money(v, undefined, undefined, 4) ?? ''
}

const bidPreview = (): AgGridColumnDef<PreviewItem> => {
    return {
        field: 'bid_preview',
        headerName: 'Bid Preview',
        valueFormatter: ({ value }) => moneyWithMoreDecimals(value),
        headerComponent: agGridComponent(DummyHeader),
        align: 'right',
        cellClass: 'justify-end',
    }
}

const defaultBidPreview = (): AgGridColumnDef<PreviewItem> => {
    return {
        field: 'default_bid_preview',
        headerName: 'Bid Preview',
        valueFormatter: ({ value }) => moneyWithMoreDecimals(value),
        headerComponent: agGridComponent(DummyHeader),
        align: 'right',
        cellClass: 'justify-end',
    }
}

const budgetPreview = (): AgGridColumnDef<PreviewItem> => {
    return {
        field: 'budget_preview',
        headerName: 'Budget Preview',
        width: 110,
        valueFormatter: ({ value }) => moneyWithMoreDecimals(value),
        headerComponent: agGridComponent(DummyHeader),
        align: 'right',
        cellClass: 'justify-end',
    }
}

const addTargetAcos = (
    columns: AgGridColumnDef<PreviewItem>[],
    time?: string,
    recipeVersion: number = 1,
): void => {
    const match = `acos_${time}`
    const acosIndex = columns.findIndex((col) => col.field === match)

    if (acosIndex === -1) {
        return
    }

    columns.splice(acosIndex, 0, {
        field: 'target_acos',
        headerName: 'Target ACOS',
        headerComponent: agGridComponent(DummyHeader),
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
            const p = { ...data }
            if (recipeVersion === 2) {
                p.target_acos = Math.round(p.target_acos * 10000 * 100) / 100
            }

            return <TargetACOS disabled target="campaign" product={p} />
        }),
    })
}

const wider = ['___reason', '___result']

const TIME_PERIOD_SENTINEL = '__time_period:'

/**
 * The value a constant variable always has, or null when the value is
 * row-dependent and must come from the row.
 *
 * The preview SQL materializes only the variables the trigger predicate reaches,
 * and constant folding erases references to literals before that set is computed
 * (MER-2795). Those columns therefore never reach the row, and formatting an
 * absent key produced NaN. A constant cannot vary per row, so reading it from
 * the metadata is correct whether or not the column is present.
 */
export const constantVariableText = (variable: VariableConfig): string | null => {
    const formula = variable.formula?.trim() ?? ''

    if (variable.type === 'time_period') {
        const unquoted = formula.replace(/^"|"$/g, '')

        return unquoted.startsWith(TIME_PERIOD_SENTINEL)
            ? unquoted.slice(TIME_PERIOD_SENTINEL.length)
            : unquoted
    }

    if (variable.type === 'number' && formula !== '' && Number.isFinite(Number(formula))) {
        return number(Number(formula), 2)
    }

    if (variable.type === 'boolean' && (formula === 'true' || formula === 'false')) {
        return formula
    }

    // A quoted literal with no interior quote, so a computed expression that
    // merely happens to start and end with one is left to the row.
    if (
        variable.type === 'string' &&
        formula.length >= 2 &&
        formula.startsWith('"') &&
        formula.endsWith('"') &&
        !formula.slice(1, -1).includes('"')
    ) {
        return formula.slice(1, -1)
    }

    // Array literals are inlined into bulk comparisons at parse time and are
    // never materialized as a column on any backend.
    if (formula.startsWith('[')) {
        return formula
    }

    return null
}

const MATCH_TYPE_FORMULAS = new Set(['match type', 'match_type', 'matchtype'])

/**
 * A variable that reads the entity's match type. PRODUCT_CATEGORY targets have
 * none — Amazon returns null — so those rows render the target's refinements
 * instead of a blank cell (MER-2293).
 */
export const isMatchTypeVariable = (variable: VariableConfig): boolean =>
    MATCH_TYPE_FORMULAS.has((variable.formula ?? '').trim().toLowerCase())

export const generateVariableColumns = (
    variables: Record<string, VariableConfig>,
): AgGridColumnDef<PreviewItem>[] => {
    const variable_cols: AgGridColumnDef<PreviewItem>[] = []

    for (const variable of Object.values(variables)) {
        const formattedName = variable.name.replaceAll('_', ' ')
        const constant = constantVariableText(variable)

        if (constant === null && isMatchTypeVariable(variable)) {
            variable_cols.push({
                field: variable.name,
                headerName: formattedName,
                headerComponent: agGridComponent(DummyHeader),
                tooltip: (
                    <TooltipContent
                        title={formattedName}
                        description={variable.formula}
                        descriptionClassName="italic text-xs"
                    />
                ),
                width: 300,
                cellRenderer: agGridComponent(({ data }: CellProps<PreviewItem>) => {
                    const value = data?.[variable.name]

                    if (value !== null && value !== undefined && value !== '') {
                        return <span>{value}</span>
                    }

                    return <span>{syntheticTargetMatchType(data) ?? ''}</span>
                }),
            })
        } else if (constant !== null) {
            variable_cols.push({
                field: variable.name,
                headerName: formattedName,
                headerComponent: agGridComponent(DummyHeader),
                tooltip: (
                    <TooltipContent
                        title={formattedName}
                        description={variable.formula}
                        descriptionClassName="italic text-xs"
                    />
                ),
                width: wider.includes(variable.name) ? 300 : undefined,
                cellRenderer: agGridComponent(() => <span>{constant}</span>),
            })
        } else if (variable.type === 'number') {
            variable_cols.push({
                field: variable.name,
                headerName: formattedName,
                headerComponent: agGridComponent(DummyHeader),
                tooltip: (
                    <TooltipContent
                        title={formattedName}
                        description={variable.formula}
                        descriptionClassName="italic text-xs"
                    />
                ),
                valueFormatter: ({ value }) => number(value, 2),
            })
        } else {
            variable_cols.push({
                field: variable.name,
                headerName: formattedName,
                headerComponent: agGridComponent(DummyHeader),
                tooltip: (
                    <TooltipContent
                        title={formattedName}
                        description={variable.formula}
                        descriptionClassName="italic text-xs"
                    />
                ),
                width: wider.includes(variable.name) ? 300 : undefined,
            })
        }
    }

    return variable_cols
}
